import './setupApiClient';
import React from 'react';
import ReactDOM from 'react-dom/client';
import '@shopify/polaris/build/esm/styles.css';
// Theme system: tokens + Polaris dark gap patches. Must come AFTER the
// Polaris stylesheet so the patches out-cascade it. See docs/THEMING.md.
import './theme.css';
import './index.css';
import App from './App';
import { SW_UPDATE_EVENT } from './hooks/useAppUpdateMonitor';
import { isReturnsHost } from './utils/returnsHost';
// Static, not lazy: it has to be on hand when the lazy wallboard import fails.
import WallboardErrorBoundary from './wallboard/WallboardErrorBoundary';

// Simple routing - check if we're on the /track path or /job path.
// Support hash-based URLs and base paths (e.g. /app/track/preorder/X/Y or #/track/...).
function getEffectivePath() {
  const p = window.location.pathname || '';
  const hash = (window.location.hash || '').replace(/^#/, '');
  if ((p === '/' || p === '') && hash.startsWith('/track')) return hash;
  return p;
}
const path = getEffectivePath();
const isTrackingPage = path.includes('/track');
const isJobFormPage = (path === '/job' || path === '/job/') || (window.location.pathname === '/' && (window.location.hash || '').replace(/^#/, '') === '/job');
// Hidden TV wallboard route — standalone page, deliberately not in any nav.
const cleanPath = path.replace(/\/+$/, '');
const isWallboardPage = cleanPath === '/3dprinter-wallboard';
/*
 * Customer locker collection — /collect#<token>. The token lives in the
 * fragment, so `getEffectivePath()` must not be used here: it treats a hash as
 * a route when the path is "/", and a 43-character token is not a route.
 * Matching on the real pathname keeps the two apart.
 */
const isLockerCollectPage = (window.location.pathname || '').replace(/\/+$/, '') === '/collect';
// Hidden staff page to approve/revoke display pairings (uses the session cookie).
const isWallboardDevicesPage = cleanPath === '/wallboard-devices';
/*
 * Customer returns portal (Ship). Public on every host; the API gates on the
 * order number + email match and on the returnsPortal switch.
 *
 * The three returns.* domains serve this same build, and a customer typing
 * returns.centralcoastcoffee.com.au lands on "/", not "/returns" — so on a
 * returns host the bare root renders the portal as well. Every other route
 * (/track, /job, /collect, the wallboards) is untouched, and the staff-app
 * host gate below still blanks the staff shell on those domains.
 */
const returnsPath = (window.location.pathname || '').replace(/\/+$/, '');
const onReturnsHost = isReturnsHost(window.location.hostname);
const isReturnsPage = returnsPath === '/returns'
  || (onReturnsHost && returnsPath === '');

// Lazy so the wallboard bundle is only fetched on its own route (and never
// weighs down the main app).
const PrinterWallboard = React.lazy(() => import('./wallboard/PrinterWallboard'));
// Lazy so a customer opening a locker link downloads this page and nothing
// else — they are on mobile data, outdoors, and the whole warehouse bundle
// would be a slow first paint at exactly the wrong moment.
const PublicLockerCollect = React.lazy(() => import('./components/collect/PublicLockerCollect'));
// Lazy for the same reason: the customer job tracker and intake form (and
// their print/constants helpers) are only needed on /track and /job, so the
// staff app no longer carries them in the entry bundle.
const PublicJobTracker = React.lazy(() => import('./components/jobs/PublicJobTracker'));
const PublicJobForm = React.lazy(() => import('./components/jobs/PublicJobForm'));
const PublicReturnsForm = React.lazy(() => import('./components/returns/PublicReturnsForm'));
const WallboardAccessGate = React.lazy(() => import('./wallboard/WallboardAccessGate'));
const WallboardDeviceAdmin = React.lazy(() => import('./wallboard/tv/WallboardDeviceAdmin'));

const darkFallback = <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, background: '#04060c' }} />;

/*
 * Staff-app host gate (2026-08-22). warehouse.phaserfpv.com.au sits behind a
 * Cloud Armor IP allowlist, but the same build is served on every other
 * hostname (status.*, jobs.*, *.web.app — Firebase Hosting can't split by
 * host), where the staff shell and login form used to render for anyone.
 * On those hosts the public routes above (/track, /job, /collect, wallboard)
 * still work exactly as before; only the default staff-app branch is blanked.
 * This is presentation-layer only — the API's own IP allowlist + auth remain
 * the real gates.
 */
const STAFF_APP_HOSTS = new Set([
  'warehouse.phaserfpv.com.au',
  // Tailscale proxy (tailnet-only hostname; egresses from an allowlisted IP).
  'warehouse.taile5c1c0.ts.net',
  'localhost',
  '127.0.0.1'
]);
const isStaffAppHost = STAFF_APP_HOSTS.has(window.location.hostname)
  || process.env.NODE_ENV === 'development';

// One production URL (/3dprinter-wallboard) serves every display, behind the
// access gate: staff session / paired device / pairing screen on warehouse
// IPs / generic denial elsewhere. Any modern browser (the warehouse TV is now
// driven by a 4K Windows PC) authorises with the 6-digit pairing code — see
// docs/SAMSUNG_TV_WALLBOARD_RETIRED.md for the Tizen build that used to live here.
let rootElement;
if (isLockerCollectPage) {
  rootElement = (
    <React.Suspense fallback={<div />}>
      <PublicLockerCollect />
    </React.Suspense>
  );
} else if (isReturnsPage) {
  rootElement = (
    <React.Suspense fallback={<div />}>
      <PublicReturnsForm />
    </React.Suspense>
  );
} else if (isTrackingPage) {
  rootElement = (
    <React.Suspense fallback={<div />}>
      <PublicJobTracker />
    </React.Suspense>
  );
} else if (isJobFormPage) {
  rootElement = (
    <React.Suspense fallback={<div />}>
      <PublicJobForm />
    </React.Suspense>
  );
} else if (isWallboardPage) {
  // The boundary sits OUTSIDE Suspense: a lazy chunk that 404s mid-deploy
  // rejects the import and throws through Suspense, and with nobody at the TV
  // the boundary's timed reload is the only way the wall comes back.
  rootElement = (
    <WallboardErrorBoundary>
      <React.Suspense fallback={darkFallback}>
        <WallboardAccessGate>
          <PrinterWallboard />
        </WallboardAccessGate>
      </React.Suspense>
    </WallboardErrorBoundary>
  );
} else if (isWallboardDevicesPage) {
  rootElement = (
    <React.Suspense fallback={<div />}>
      <WallboardDeviceAdmin />
    </React.Suspense>
  );
} else if (!isStaffAppHost) {
  // Wrong hostname for the staff app: render nothing at all.
  rootElement = darkFallback;
} else {
  rootElement = <App />;
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    {rootElement}
  </React.StrictMode>
);

function registerServiceWorker() {
  if (!('serviceWorker' in navigator) || process.env.NODE_ENV !== 'production') return;

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        console.log('[SW] Service Worker registered:', registration.scope);

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              window.dispatchEvent(new CustomEvent(SW_UPDATE_EVENT));
            }
          });
        });

        // Check for updates when the tab becomes active again.
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            registration.update().catch(() => {});
          }
        });
      })
      .catch((error) => {
        console.error('[SW] Service Worker registration failed:', error);
      });
  });
}

registerServiceWorker();
